<template>
  <CCanvasModal
    show
    :title="
      theme?.id
        ? t('canvasUI.CCreateThemeModal.editTitle', { name: theme?.name })
        : t('canvasUI.CCreateThemeModal.createTitle')
    "
    @close="emit('cancel')"
  >
    <NSteps v-model:current="currentStep" class="mx-1 mt-1 mb-8" size="small">
      <NStep :title="t('canvasUI.CCreateThemeModal.steps.settings')" />
      <NStep :disabled="!stepsValidity[0]" :title="t('canvasUI.CCreateThemeModal.steps.colors')" />
      <NStep :disabled="!stepsValidity[0]" :title="t('canvasUI.CCreateThemeModal.steps.typography')" />
      <NStep
        v-if="launchDarkly.enable_theme_assets"
        :disabled="!stepsValidity[0]"
        :title="t('canvasUI.CCreateThemeModal.steps.assets')"
      />
    </NSteps>

    <div v-if="currentStep === 1">
      <CThemeSettingsStep
        v-model="form.settings"
        :font-options="fontOptions"
        @update:font="onUpdateFont"
        @validation="onThemeSettingsValidation"
      />
    </div>

    <CThemeColorsStep v-if="currentStep === 2" v-model="form.colors" />
    <CThemeTypographyStep
      v-if="currentStep === 3"
      v-model="form.typography"
      :font-options="fontOptions"
      :palette="form.colors.palette"
    />
    <CThemeAssetsStep v-if="currentStep === 4 && launchDarkly.enable_theme_assets" v-model="form.assets" />

    <template #footer>
      <div class="flex justify-between gap-2 w-full">
        <CButton @click="emit('cancel')">
          {{ t('canvasUI.common.cancel') }}
        </CButton>

        <div class="flex gap-2">
          <CButton v-if="currentStep > 1" :loading="isLoading" @click="currentStep--">
            {{ t('canvasUI.common.back') }}
          </CButton>

          <CButton
            v-if="(launchDarkly.enable_theme_assets ? currentStep < 4 : currentStep < 3) && stepsValidity[0]"
            :loading="isLoading"
            @click="handleNext"
          >
            {{ t('canvasUI.common.next') }}
          </CButton>

          <CButton :disabled="!stepsValidity[0]" :loading="isLoading" type="primary" @click="handleSubmit">
            {{ t('canvasUI.common.done') }}
          </CButton>
        </div>
      </div>
    </template>
  </CCanvasModal>
</template>

<script lang="ts" setup>
import { isEmpty, orderBy } from 'lodash-es'
import { NSteps, NStep } from 'naive-ui'
import { computed, inject, reactive, ref, onMounted, type ComputedRef } from 'vue'
import { useI18n } from 'vue-i18n'

import CButton from '@lib/components/CButton/CButton.vue'
import CCanvasModal from '@lib/components/CCanvasModal/CCanvasModal.vue'
import CThemeSettingsStep from './steps/CThemeSettingsStep.vue'
import CThemeColorsStep from './steps/CThemeColorsStep.vue'
import CThemeTypographyStep from './steps/CThemeTypographyStep.vue'
import CThemeAssetsStep from './steps/CThemeAssetsStep.vue'

import useCanvasTheme from '@lib/apps/canvas-builder/composables/useCanvasTheme'

import type { CanvasThemeFormData } from '@lib/types/canvasTheme'
import {
  CanvasThemeStatus,
  type CanvasThemeRetrieve,
  type CanvasThemeSettings,
  type CanvasTypographyPreset,
  type CanvasThemeAsset,
} from '@lib/types/canvasTheme'
import { DEFAULT_GLOBAL_COMPONENT_SPACING } from '@canvas-builder/constants/theme.const'
import { DEFAULT_FONT } from '@canvas-builder/constants'
import type { SelectOptionActionUrl } from '@lib/theme/theme.types'
import type { FileRetrieve } from '@openapi'
import type { LaunchDarklyEnv } from '@lib/types/launchDarkly.types'

const props = withDefaults(
  defineProps<{
    theme?: CanvasThemeRetrieve
    isLoading?: boolean
    listFiles: (params: any) => Promise<FileRetrieve[]>
  }>(),
  {
    isLoading: false,
  },
)

const emit = defineEmits<{
  (e: 'cancel'): void
  (e: 'create', form: CanvasThemeFormData): void
  (e: 'update', form: CanvasThemeFormData): void
}>()

const launchDarkly = inject<ComputedRef<LaunchDarklyEnv>>(
  'launchDarkly',
  computed(() => ({})),
)

const { t } = useI18n()
const { customFonts, getDefaultTypographyConfiguration, loadCustomFonts } = useCanvasTheme()

const maxSteps = computed(() => (launchDarkly.value.enable_theme_assets ? 4 : 3))

const fontOptions = ref<SelectOptionActionUrl[]>([])

const currentStep = ref(1)
const stepsValidity = ref([false, true, true, true])

const form: {
  settings: {
    name: CanvasThemeRetrieve['name']
    status: CanvasThemeRetrieve['status']
    componentSpacing: CanvasThemeSettings['component_spacing']
  }
  colors: Pick<CanvasThemeSettings, 'background_color' | 'palette'>
  typography: Record<string, CanvasTypographyPreset>
  assets: {
    logo?: CanvasThemeAsset
    images: CanvasThemeAsset[]
  }
} = reactive({
  settings: {
    name: props.theme?.name ?? '',
    status: props.theme?.status ?? CanvasThemeStatus.DRAFT,
    componentSpacing: props.theme?.settings?.component_spacing ?? DEFAULT_GLOBAL_COMPONENT_SPACING,
  },
  colors: {
    background_color: props.theme?.settings?.background_color ?? '#FFFFFF',
    palette: props.theme?.settings?.palette ?? [],
  },
  typography: !isEmpty(props.theme?.typography) ? props.theme?.typography : getDefaultTypographyConfiguration(),
  assets: {
    logo: props.theme?.logo,
    images: props.theme?.assets ?? [],
  },
})

function handleSubmit() {
  if (props.theme?.id) {
    emit('update', form)
  } else {
    emit('create', form)
  }
}

function handleNext() {
  if (currentStep.value < maxSteps.value) {
    currentStep.value++
  }
}

function onThemeSettingsValidation(isValid: boolean) {
  stepsValidity.value[0] = isValid
}

function onUpdateFont(font: SelectOptionActionUrl) {
  fontOptions.value = [...orderBy([...fontOptions.value, font], ['label'])]
}

onMounted(async () => {
  if (!customFonts.value?.length) {
    await loadCustomFonts(props.listFiles)
  }

  fontOptions.value = [
    ...orderBy(
      [
        { label: DEFAULT_FONT, value: DEFAULT_FONT },
        ...customFonts.value.map(({ name, content_url }) => ({
          label: name ?? '',
          value: name ?? '',
          url: content_url,
        })),
      ],
      ['label'],
    ),
  ]
})
</script>
